import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_SITE_RADIUS,
  MIN_SITE_RADIUS,
  distanceMeters,
} from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { num, plural, WORDS } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';

interface SiteRow {
  id: string;
  name: string;
  address: string | null;
  lat: number;
  lng: number;
  radiusMeters: number;
  isActive: boolean;
}

/**
 * مواقعُ العمل التي يُقبل منها تسجيلُ الحضور.
 *
 * نقطةٌ ونصفُ قطر — لا رسمُ حدودٍ على خريطة: من يضبطها يعرف عنوانَ مقرّه،
 * وواجهةُ رسمِ مضلَّعٍ تحتاج مزوّدَ خرائطَ ومفتاحًا لكلّ شركة، وتُنتج
 * الدقّةَ نفسَها في النهاية.
 *
 * والإحداثيّاتُ تُلتقط من هنا بزرٍّ: من يقف في مقرّه يضغط فيُملأ الحقلان
 * — أهونُ من نسخهما من خرائط جوجل، وأقلُّ خطأً.
 */
export function WorkSites() {
  return (
    <div className="page">
      <PageHead
        title="مواقع العمل"
        subtitle="من أين يُقبل تسجيل الحضور والانصراف"
      />
      <SitesSection />
    </div>
  );
}

/**
 * المواقعُ قسمًا لا صفحة.
 *
 * تُعرض في صفحتها هنا، وتُعرض في «الشركة» تحت الإعدادات إلى جانب العملة
 * وخطط العمل — لأنّ من يضبط مقرَّ الشركة يضبط نطاقَ حضوره في الجلسة نفسِها.
 * ونسخُها في مكانين كان سيُنتج شاشتين تفترقان عند أوّل تعديل.
 */
export function SitesSection() {
  const { atLeast } = useAuth();
  const canEdit = atLeast('ADMIN');
  const [editing, setEditing] = useState<SiteRow | 'new' | null>(null);

  const list = useQuery({
    queryKey: ['hr', 'sites'],
    queryFn: () => api<SiteRow[]>('/hr/sites'),
  });

  const rows = list.data ?? [];
  const active = rows.filter((r) => r.isActive);

  return (
    <>
      <div className="card">
        <div className="card-head">
          <h2 className="card-title">مواقع العمل</h2>
          <span className="tiny mute">من أين يُقبل تسجيل الحضور والانصراف</span>
          <span style={{ marginInlineStart: 'auto' }}>
            {canEdit && (
              <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
                <Icon name="plus" size={15} />
                موقع جديد
              </button>
            )}
          </span>
        </div>
      </div>

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}

      {/*
        الحدُّ يُكتب في الشاشة لا يُكتشف بالتجربة: تحديدُ المتصفّح يعمل على
        HTTPS أو على الجهاز نفسِه فقط، ومن يفتح النظام بعنوان الشبكة
        الداخلية لن يعمل عنده الزرُّ مهما ضبط المواقع.
      */}
      <Notice title="الموقع يحتاج اتصالًا مؤمَّنًا" icon="alert" kind="warning">
        المتصفّحات لا تعطي الموقع إلّا على <span dir="ltr">https</span> أو على
        الجهاز نفسِه (<span dir="ltr">localhost</span>). فإن فُتح النظام
        بعنوان شبكةٍ داخليّةٍ بلا شهادة، لن يستطيع أحدٌ تسجيل حضوره.
      </Notice>

      {active.length === 0 && rows.length > 0 && (
        <Notice title="لا موقع مفعَّل" icon="alert" kind="warning">
          كلُّ المواقع موقوفة، فيُسجَّل الحضور بلا تحقّقٍ من المكان.
        </Notice>
      )}

      {list.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا مواقع بعد"
          text="بلا موقعٍ مضبوطٍ يُسجَّل الحضور من أيّ مكان — ويُقال ذلك للموظّف صراحةً بدل أن يُدَّعى تحقّقٌ لم يقع."
          action={
            canEdit ? (
              <button className="btn btn-primary" onClick={() => setEditing('new')}>
                أضف موقعًا
              </button>
            ) : undefined
          }
        />
      )}

      {rows.length > 0 && (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>الموقع</th>
                  <th>الإحداثيّات</th>
                  <th>النطاق</th>
                  <th>الحالة</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <span className="cell-strong">{s.name}</span>
                      {s.address && <div className="tiny mute">{s.address}</div>}
                    </td>
                    <td className="num cell-mute" dir="ltr">
                      {s.lat.toFixed(6)}, {s.lng.toFixed(6)}
                    </td>
                    <td className="num">{num(s.radiusMeters)} م</td>
                    <td>
                      <Badge kind={s.isActive ? 'success' : 'neutral'}>
                        {s.isActive ? 'مفعَّل' : 'موقوف'}
                      </Badge>
                    </td>
                    <td>
                      {canEdit && (
                        <button
                          className="btn btn-sm"
                          onClick={() => setEditing(s)}
                        >
                          تعديل
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <SiteDialog
          site={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function SiteDialog({
  site,
  onClose,
}: {
  site: SiteRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [name, setName] = useState(site?.name ?? '');
  const [address, setAddress] = useState(site?.address ?? '');
  const [lat, setLat] = useState(site ? String(site.lat) : '');
  const [lng, setLng] = useState(site ? String(site.lng) : '');
  const [radius, setRadius] = useState(String(site?.radiusMeters ?? 200));
  const [isActive, setIsActive] = useState(site?.isActive ?? true);
  const [picking, setPicking] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);
  const [accuracy, setAccuracy] = useState<number | null>(null);

  const valid =
    name.trim().length >= 2 &&
    lat.trim() !== '' &&
    lng.trim() !== '' &&
    Number.isFinite(Number(lat)) &&
    Number.isFinite(Number(lng));

  /* المسافةُ من الموقع المحفوظ إلى نقطتي الآن — تصحيحٌ يُرى قبل الحفظ. */
  const drift =
    site && accuracy !== null
      ? distanceMeters(
          { lat: site.lat, lng: site.lng },
          { lat: Number(lat), lng: Number(lng) },
        )
      : null;

  const pick = () => {
    if (!('geolocation' in navigator)) {
      setPickError('متصفّحك لا يعطي الموقع.');
      return;
    }
    setPicking(true);
    setPickError(null);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setLat(p.coords.latitude.toFixed(6));
        setLng(p.coords.longitude.toFixed(6));
        setAccuracy(Math.round(p.coords.accuracy));
        setPicking(false);
      },
      () => {
        setPicking(false);
        setPickError(
          'تعذّر تحديد موقعك. اسمح بالموقع في متصفّحك، أو اكتب الإحداثيّات يدويًّا من خرائط جوجل.',
        );
      },
      { enableHighAccuracy: true, timeout: 15_000 },
    );
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        address: address.trim() || null,
        lat: Number(lat),
        lng: Number(lng),
        radiusMeters: Number(radius) || 200,
        isActive,
      };
      return site
        ? api(`/hr/sites/${site.id}`, { method: 'PATCH', body })
        : api('/hr/sites', { method: 'POST', body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['hr'] });
      toast(site ? 'حُفظ الموقع' : 'أُضيف الموقع');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={site ? `تعديل ${site.name}` : 'موقع عمل جديد'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="ws-name">
            اسم الموقع
          </label>
          <input
            id="ws-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="المقرّ الرئيسي"
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="ws-addr">
            العنوان
          </label>
          <input
            id="ws-addr"
            className="input"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="٢٧ شارع النصر، المعادي"
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="ws-lat">
            خط العرض
          </label>
          <input
            id="ws-lat"
            className="input num"
            dir="ltr"
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            placeholder="30.012345"
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="ws-lng">
            خط الطول
          </label>
          <input
            id="ws-lng"
            className="input num"
            dir="ltr"
            value={lng}
            onChange={(e) => setLng(e.target.value)}
            placeholder="31.234567"
          />
        </div>

        <div className="field span-2">
          <button
            type="button"
            className="btn"
            onClick={pick}
            disabled={picking}
          >
            <Icon name="mapPin" size={15} />
            {picking ? 'جارٍ التحديد…' : 'خذ إحداثيّاتي الآن'}
          </button>

          {pickError && (
            <span className="tiny" style={{ color: 'var(--danger)' }}>
              {pickError}
            </span>
          )}

          {accuracy !== null && (
            <span className="tiny mute">
              دقّة التحديد ±{accuracy} متر.
              {drift !== null && drift > 0
                ? ` وأنت على بُعد ${drift} مترًا من الإحداثيّات المحفوظة.`
                : ''}
            </span>
          )}

          <span className="tiny mute">
            أو انسخهما من خرائط جوجل: اضغط مطوّلًا على المكان فيظهر الرقمان.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="ws-radius">
            نطاق القبول بالأمتار
          </label>
          <input
            id="ws-radius"
            className="input num"
            type="number"
            min={MIN_SITE_RADIUS}
            max={MAX_SITE_RADIUS}
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
          />
          <span className="tiny mute">
            تحديدُ المتصفّح يخطئ عشراتِ الأمتار داخل المباني — ونطاقٌ ضيّقٌ
            يمنع من يقف في مكتبه.
          </span>
        </div>

        <div className="field">
          <label className="row tiny" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            مفعَّل
          </label>
          <span className="tiny mute">
            الإيقافُ لا الحذف — صفوفُ الحضور تشير إليه.
          </span>
        </div>
      </div>

      <p className="tiny mute mt-4">
        {plural(1, WORDS.record)} من هذه المواقع يكفي ليصير التحقّق ملزمًا:
        بمجرّد وجود موقعٍ مفعَّلٍ واحدٍ يُردّ كلُّ تسجيلٍ من خارج النطاق.
      </p>
    </Modal>
  );
}
